import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router, ActivatedRoute, RouterLink } from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSnackBarModule, MatSnackBar } from '@angular/material/snack-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDividerModule } from '@angular/material/divider';
import { BookingService } from '../../../core/api/booking.service';
import { CarService } from '../../../core/api/car.service';
import { BookingCreate } from '../../../core/models/booking.model';
import { CarAvailability } from '../../../core/models/car.model';
import { parseApiDateTime, toApiDateTime } from '../../../shared/utils/date.utils';
import { DatetimeInputComponent } from '../../../shared/components/datetime-input/datetime-input.component';
import { CarSelectComponent } from '../../../shared/components/car-select/car-select.component';
import { ClientSelectComponent } from '../../../shared/components/client-select/client-select.component';

@Component({
  selector: 'app-booking-create',
  standalone: true,
  imports: [
    CommonModule, ReactiveFormsModule, MatCardModule, MatIconModule, MatButtonModule,
    MatFormFieldModule, MatInputModule,
    MatSnackBarModule, MatProgressSpinnerModule,
    MatDividerModule,
    RouterLink,
    DatetimeInputComponent,
    CarSelectComponent,
    ClientSelectComponent,
  ],
  templateUrl: './booking-create.component.html',
  styleUrl: './booking-create.component.scss',
})
export class BookingCreateComponent implements OnInit {
  form: FormGroup;
  submitting = false;
  availableCars: CarAvailability[] = [];

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private bookingService: BookingService,
    private carService: CarService,
    private snackBar: MatSnackBar,
  ) {
    this.form = this.fb.group({
      client: [null as number | null, Validators.required],
      car: [null, Validators.required],
      startDate: [null as Date | null, Validators.required],
      endDate: [null as Date | null, Validators.required],
      depositAmount: [0],
      rentalAmount: [0],
      notes: [''],
    });
  }

  ngOnInit(): void {
    this.loadCars();
    const carId = this.route.snapshot.queryParamMap.get('car');
    if (carId) {
      this.form.patchValue({ car: Number(carId) });
    }
  }

  onSubmit(): void {
    if (this.form.invalid || this.submitting) return;
    this.submitting = true;
    const v = this.form.value;
    const startDate = v.startDate ? parseApiDateTime(v.startDate) : null;
    const endDate = v.endDate ? parseApiDateTime(v.endDate) : null;
    if (!startDate || !endDate) {
      this.form.markAllAsTouched();
      this.submitting = false;
      return;
    }
    const data: BookingCreate = {
      client: v.client,
      car: v.car,
      start_date: toApiDateTime(startDate),
      end_date: toApiDateTime(endDate),
      deposit_amount: String(v.depositAmount || 0),
      rental_amount: String(v.rentalAmount || 0),
      notes: v.notes || '',
    };
    this.bookingService.create(data).subscribe({
      next: (b) => {
        this.router.navigate(['/bookings', b.id]);
        this.snackBar.open('Бронирование создано', 'ОК', { duration: 3000 });
      },
      error: (err: any) => {
        this.submitting = false;
        const errorData = err.error;
        if (errorData && typeof errorData === 'object' && !Array.isArray(errorData)) {
          this.setServerErrors(errorData);
          const nonField = errorData.all || errorData.non_field_errors;
          if (nonField) {
            const msg = Array.isArray(nonField) ? nonField[0] : String(nonField);
            this.snackBar.open(msg, 'ОК', { duration: 5000 });
          }
        } else {
          const msg = Array.isArray(errorData) ? errorData[0] : 'Ошибка создания';
          this.snackBar.open(msg, 'ОК', { duration: 5000 });
        }
      },
    });
  }

  private loadCars(): void {
    const today = new Date().toISOString().split('T')[0];
    this.carService.getAvailability(today).subscribe(cars => {
      this.availableCars = cars;
    });
  }

  private readonly apiFieldToControl: Record<string, string> = {
    client: 'client',
    car: 'car',
    start_date: 'startDate',
    end_date: 'endDate',
    deposit_amount: 'depositAmount',
    rental_amount: 'rentalAmount',
    notes: 'notes',
  };

  private setServerErrors(errors: Record<string, any>): void {
    for (const [apiField, controlName] of Object.entries(this.apiFieldToControl)) {
      const control = this.form.get(controlName);
      if (control && errors.hasOwnProperty(apiField)) {
        const messages = errors[apiField];
        const msg = Array.isArray(messages) ? messages[0] : String(messages);
        control.setErrors({ ...control.errors, serverError: msg });
      }
    }
  }
}
